import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { getPublicQualificationSession, submitPublicQualification } from '../../services/marketingApi';
import { PRODUCT_LINE_QUESTION, getQuestionsForLine } from '../../utils/leadQualification';

const initialState = { loading: true, error: '', active: false };

export default function PublicQualificationWizard() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [remote, setRemote] = useState(initialState);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) { setRemote({ loading: false, error: 'Enlace inválido.', active: false }); return undefined; }
    getPublicQualificationSession(token)
      .then((data) => { if (active) setRemote({ loading: false, error: '', active: data?.active === true }); })
      .catch((error) => active && setRemote({ loading: false, error: error.message || 'No se pudo abrir este enlace.', active: false }));
    return () => { active = false; };
  }, [token]);

  const steps = useMemo(() => {
    const line = answers.productLine;
    return [PRODUCT_LINE_QUESTION, ...(line ? getQuestionsForLine(line) : [])];
  }, [answers.productLine]);
  const current = steps[step];
  const canContinue = current && (current.multi ? Array.isArray(answers[current.id]) && answers[current.id].length > 0 : Boolean(answers[current.id]));

  const choose = (question, value) => {
    if (question.id === 'productLine' && answers.productLine !== value) setStep(0);
    setAnswers((currentAnswers) => {
      if (!question.multi) {
        const next = { ...currentAnswers, [question.id]: value };
        if (question.id === 'productLine' && currentAnswers.productLine !== value) {
          Object.keys(next).forEach((key) => { if (key !== 'productLine') delete next[key]; });
        }
        return next;
      }
      const selected = new Set(currentAnswers[question.id] || []);
      selected.has(value) ? selected.delete(value) : selected.add(value);
      return { ...currentAnswers, [question.id]: [...selected] };
    });
  };

  const next = async () => {
    if (!canContinue) return;
    if (step < steps.length - 1) { setStep((value) => value + 1); return; }
    setSubmitting(true); setRemote((value) => ({ ...value, error: '' }));
    try {
      await submitPublicQualification({ token, answers });
      setCompleted(true);
    } catch (error) {
      setRemote((value) => ({ ...value, error: error.message || 'No se pudieron guardar tus respuestas.' }));
    } finally { setSubmitting(false); }
  };

  if (remote.loading) return <PageShell><p className="text-center text-slate-500">Preparando tus preguntas…</p></PageShell>;
  if (completed) return <PageShell><div className="text-center"><CheckCircleIcon className="mx-auto h-14 w-14 text-emerald-500" /><h1 className="mt-4 text-2xl font-black text-slate-900">¡Gracias por contarnos!</h1><p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-600">Un asesor de BBTS revisará lo que necesitas y se comunicará contigo para ayudarte.</p></div></PageShell>;
  if (!remote.active) return <PageShell><p className="text-center text-sm text-red-700">{remote.error || 'Este enlace ya no está disponible.'}</p></PageShell>;

  return <PageShell>
    <p className="text-center text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">BBTS</p>
    <h1 className="mt-2 text-center text-2xl font-black text-slate-900">Soluciones integrales para empresas y restaurantes</h1>
    <p className="mx-auto mt-2 max-w-md text-center text-sm leading-6 text-slate-600">Responde unas preguntas cortas para identificar el punto de partida adecuado para tu operación.</p>
    {remote.error && <p role="alert" className="mt-5 rounded-lg bg-red-50 p-3 text-sm text-red-700">{remote.error}</p>}
    <div className="mt-7"><div className="mb-3 flex items-center justify-between text-xs font-semibold text-slate-500"><span>Pregunta {step + 1} de {steps.length}</span><span>{Math.round(((step + 1) / steps.length) * 100)}%</span></div><div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div></div>
    {current && <section className="mt-7"><h2 className="text-lg font-bold text-slate-900">{current.label}</h2>{current.multi && <p className="mt-1 text-xs text-slate-500">Puedes seleccionar más de una opción.</p>}<div className="mt-4 space-y-3">{current.options.map((option) => { const selected = current.multi ? (answers[current.id] || []).includes(option.value) : answers[current.id] === option.value; return <button type="button" key={option.value} onClick={() => choose(current, option.value)} className={`w-full rounded-xl border px-4 py-3 text-left text-sm font-semibold transition ${selected ? 'border-emerald-500 bg-emerald-50 text-emerald-900 ring-1 ring-emerald-500' : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300'}`}><span className={`mr-3 inline-flex h-5 w-5 items-center justify-center border ${current.multi ? 'rounded' : 'rounded-full'} ${selected ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300'}`}>{selected ? '✓' : ''}</span>{option.label}</button>; })}</div></section>}
    <div className="mt-8 flex gap-3"><button type="button" onClick={() => setStep((value) => Math.max(0, value - 1))} disabled={step === 0 || submitting} className="rounded-lg border border-slate-200 px-4 py-3 text-sm font-bold text-slate-600 disabled:opacity-40">Atrás</button><button type="button" onClick={next} disabled={!canContinue || submitting} className="flex-1 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-bold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-40">{submitting ? 'Guardando…' : step === steps.length - 1 ? 'Enviar respuestas' : 'Continuar'}</button></div>
  </PageShell>;
}

function PageShell({ children }) {
  return <main className="min-h-screen bg-slate-50 px-4 py-12 sm:py-20"><div className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-9">{children}</div></main>;
}
